/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import {
  Box,
  Grid2 as Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { SortDirection } from 'graphql/generated-types'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'

import {
  DisplayCoolerTrendingProductsStatsQuery,
  DisplayCoolerTrendingProductsStatsQueryVariables,
  GET_DISPLAY,
  GET_DISPLAY_COOLER_SUMMARY_STATS,
  GET_DISPLAY_COOLER_TRENDING_PRODUCTS_STATS,
  GetDisplayCoolerProductStatsQueryVariables,
  GetDisplayCoolerSummaryStatsQuery,
  GetDisplayCoolerSummaryStatsQueryVariables,
  GetDisplayQuery,
  GetDisplayQueryVariables,
} from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import { useSetDefaultTimeRange } from '@/hooks/useSetDefaultTimeRange'
import { useTableOrder } from '@/hooks/useTableOrder'
import { useTablePageState } from '@/hooks/useTablePageState'
import useT from '@/i18n/useT'
import { DateHelpers } from '@/utils/date.helpers'

import { Filter } from '../components/FilterBar'
import CoolerDetailHeader from './components/CoolerDetailHeader'
import CoolerPlanRealPanel from './components/CoolerPlanRealPanel'
import CoolerProductStatsTable from './components/CoolerProductStatsTable'
import CoolerTopStats from './components/CoolerTopStats'
import PlanogramPerformanceHistory from './components/PlanogramPerformanceHistory'
import useCoolerProductStats from './hooks/useCoolerProductStats'

type DisplayCoolerProductStatsOrdering = NonNullable<
  GetDisplayCoolerProductStatsQueryVariables['ordering']
>

const CoolerDetail: React.FC = () => {
  const theme = useTheme()
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'))
  const { t } = useT()
  const { id: storeId, displayId } = useParams()
  const {
    state: urlState,
    handle: { handleUpdateSearchParams, handleNavTo },
  } = useUrlSearchParams<{ startDate?: string; endDate?: string }>()

  if (!storeId || !displayId) {
    throw Error('Need store and display id in url')
  }

  const filters: Filter[] = [
    {
      label: t('common.other'),
      type: 'preCannedDatePicker',
      options: { startDateKey: 'startDate', endDateKey: 'endDate' },
    },
    {
      label: t('common.startDate'),
      type: 'date',
      options: { urlKey: 'startDate' },
    },
    {
      label: t('common.endDate'),
      type: 'date',
      options: { urlKey: 'endDate' },
    },
  ]

  const { startDate, endDate } = urlState

  useSetDefaultTimeRange({
    startDate,
    endDate,
    updateSearchParams: handleUpdateSearchParams,
  })

  const startDateWithTime = startDate
    ? DateHelpers.getStartOfDayWithTimezone(startDate)
    : null
  const endDateWithTime = endDate
    ? DateHelpers.getEndOfDayWithTimezone(endDate)
    : null

  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 25,
  })

  const { order, orderBy, handleRequestSort, setOrder, setOrderBy } =
    useTableOrder<DisplayCoolerProductStatsOrdering>(
      'revenue',
      SortDirection.DESC,
    )

  const { data: displayData } = useQuery<
    GetDisplayQuery,
    GetDisplayQueryVariables
  >(GET_DISPLAY, { variables: { id: displayId } })

  const {
    data: summaryData,
    loading: summaryLoading,
    error: summaryError,
  } = useQuery<
    GetDisplayCoolerSummaryStatsQuery,
    GetDisplayCoolerSummaryStatsQueryVariables
  >(GET_DISPLAY_COOLER_SUMMARY_STATS, {
    variables: {
      displayId,
      startDate: startDateWithTime || '',
      endDate: endDateWithTime || '',
    },
    skip: !startDateWithTime || !endDateWithTime,
    fetchPolicy: 'cache-and-network',
  })
  const {
    data: trendingProductsData,
    loading: trendingProductsLoading,
    error: trendingProductsError,
  } = useQuery<
    DisplayCoolerTrendingProductsStatsQuery,
    DisplayCoolerTrendingProductsStatsQueryVariables
  >(GET_DISPLAY_COOLER_TRENDING_PRODUCTS_STATS, {
    variables: {
      displayId,
      startDate: startDateWithTime || '',
      endDate: endDateWithTime || '',
    },
    skip: !startDateWithTime || !endDateWithTime,
    fetchPolicy: 'cache-and-network',
  })

  const { rows, statsByUpc, loading, error, totalCount } =
    useCoolerProductStats({
      displayId,
      startDate: startDateWithTime || '',
      endDate: endDateWithTime || '',
      page,
      rowsPerPage,
      orderBy,
      order: order as 'ASC' | 'DESC',
    })

  const shapedUpcs = displayData?.display.planogram?.shapedUpcs || []
  const [hoveredProductUpc, setHoveredProductUpc] = useState<string | null>(
    null,
  )
  const heatMapValuesByUpc = Object.fromEntries(
    Object.entries(statsByUpc).map(([upc, value]) => [
      upc,
      value?.revenue ?? 0,
    ]),
  )

  const handleComplianceClick = () => {
    handleNavTo(`/stores/${storeId}/displays/${displayId}/compliance`, {
      resetParams: false,
    })
  }

  // TODO: remove this once we fix ai insights
  const { user } = useLoggedInUser()
  const showDemoAiInsights =
    user?.tenant?.name.toLowerCase().includes('trailhead market') ||
    user?.tenant?.name.toLowerCase().includes('outer trails')

  return (
    <>
      <Helmet title={displayData?.display.name || t('coolerDetail.title')} />
      <CoolerDetailHeader storeId={storeId} filters={filters} />
      {!isSmUp && (
        <Box mt={2} mb={3}>
          <CoolerTopStats
            // TODO: remove tenantName and coolerName once we fix ai insights
            showDemoAiInsights={!!showDemoAiInsights}
            coolerName={displayData?.display.name || ''}
            summary={summaryData?.displayCoolerSummaryStats || null}
            trendingProducts={
              trendingProductsData?.displayCoolerTrendingProductsStats || null
            }
            t={t}
            onComplianceClick={handleComplianceClick}
            loading={summaryLoading || trendingProductsLoading}
            error={summaryError?.message || trendingProductsError?.message}
          />
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid size={{ xs: 12, sm: 5, lg: 4 }}>
            {startDateWithTime && endDateWithTime && (
              <CoolerPlanRealPanel
                displayId={displayId}
                shapedUpcs={shapedUpcs}
                heatMapValuesByUpc={heatMapValuesByUpc}
                productStatsByUpc={statsByUpc}
                startDate={startDateWithTime}
                endDate={endDateWithTime}
                hoveredProductUpc={hoveredProductUpc}
                fitPlanogramToWidth
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 7, lg: 8 }}>
            <Stack spacing={3}>
              {isSmUp && (
                <CoolerTopStats
                  // TODO: remove showDemoAiInsights once we fix ai insights
                  showDemoAiInsights={!!showDemoAiInsights}
                  coolerName={displayData?.display.name || ''}
                  summary={summaryData?.displayCoolerSummaryStats || null}
                  trendingProducts={
                    trendingProductsData?.displayCoolerTrendingProductsStats ||
                    null
                  }
                  loading={summaryLoading || trendingProductsLoading}
                  error={
                    summaryError?.message || trendingProductsError?.message
                  }
                  t={t}
                  onComplianceClick={handleComplianceClick}
                />
              )}
              <Box>
                <CoolerProductStatsTable
                  rows={rows}
                  totalCount={totalCount}
                  loading={loading}
                  error={error}
                  order={order}
                  orderBy={orderBy}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  setPage={setPage}
                  setRowsPerPage={setRowsPerPage}
                  handleRequestSort={handleRequestSort}
                  onRowHoverChange={setHoveredProductUpc}
                  t={t}
                />
              </Box>
              <PlanogramPerformanceHistory displayId={displayId} />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default CoolerDetail
